import type { UiNodeSpec } from './UiNodeSpec';
import type { NodeKind } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { workOrderUiNodeSpec } from './work-order.ui-spec';
import { createMaterialUiNodeSpec } from './create-material.ui-spec';
import { appletEventEmitUiNodeSpec } from './applet-event-emit.ui-spec';
import { appletEventListenUiNodeSpec } from './applet-event-listen.ui-spec';
import { attributeMaterialLinkUiNodeSpec } from './attribute-material-link.ui-spec';
import { boolCastUiNodeSpec } from './bool-cast.ui-spec';
import { booleanComparisonUiNodeSpec } from './boolean-comparison.ui-spec';
import { comparisonNumberUiNodeSpec } from './comparison-number.ui-spec';
import { comparisonStringUiNodeSpec } from './comparison-string.ui-spec';
import { defaultValUiNodeSpec } from './default-val.ui-spec';
import { eventDelayUiNodeSpec } from './event-delay.ui-spec';
import { eventOneShotUiNodeSpec } from './event-one-shot.ui-spec';
import { eventSwitchUiNodeSpec } from './event-switch.ui-spec';
import { jsonAggUiNodeSpec } from './json-agg.ui-spec';
import { jsonParseUiNodeSpec } from './json-parse.ui-spec';
import { mqttPublishUiNodeSpec } from './mqtt-publish.ui-spec';
import { mqttSubscribeUiNodeSpec } from './mqtt-subscribe.ui-spec';
import { numCastUiNodeSpec } from './num-cast.ui-spec';
import { numClampUiNodeSpec } from './num-clamp.ui-spec';
import { numberUiNodeSpec } from './number.ui-spec';
import { performanceDataCollectUiNodeSpec } from './performance-data-collect.ui-spec';
import { scriptUiNodeSpec } from './script.ui-spec';
import { sliceUiNodeSpec } from './slice.ui-spec';
import { sparkplugCommanderUiNodeSpec } from './sparkplug-commander.ui-spec';
import { sparkplugConsumerUiNodeSpec } from './sparkplug-consumer.ui-spec';
import { startUiNodeSpec } from './start.ui-spec';
import { strCastUiNodeSpec } from './str-cast.ui-spec';
import { textUiNodeSpec } from './text.ui-spec';
import { timerUiNodeSpec } from './timer.ui-spec';
import { txtFormatUiNodeSpec } from './txt-format.ui-spec';
import { udpReadUiNodeSpec } from './udp-read.ui-spec';
import { udpSocketUiNodeSpec } from './udp-socket.ui-spec';
import { udpWriteUiNodeSpec } from './udp-write.ui-spec';
import { varAssignedUiNodeSpec } from './var-assigned.ui-spec';
import { varReadUiNodeSpec } from './var-read.ui-spec';
import { varWriteUiNodeSpec } from './var-write.ui-spec';
import { calendarUiNodeSpec } from './calendar.ui-spec';
import { varWriteMultipleSpec } from './var-write-multiple.ui-spec';
import { workInstructionUiNodeSpec } from './work-instruction.ui-spec';
import { workOrderSetStatusUiNodeSpec } from './work-order-set-status.ui-spec';
import { processCollectUiNodeSpec } from './process-collect.ui-spec';
import { goToTabUiNodeSpec } from './go-to-tab.ui-spec';
import { nextTabUiNodeSpec } from './next-tab.ui-spec';
import { previousTabUiNodeSpec } from './previous-tab.ui-spec';
import { materialLookupUiNodeSpec } from './material-lookup.ui-spec';
import { functionalInputsUiNodeSpec } from './functional-inputs.ui-spec';
import { functionalSelectUiNodeSpec } from './functional-select.ui-spec';
import { functionalOutputsUiNodeSpec } from './functional-outputs.ui-spec';
import { eventThrottleUiNodeSpec } from './event-throttle.ui-spec';
import { fileReadUiNodeSpec } from './file-read.ui-spec';
import { fileWriteUiNodeSpec } from './file-write.ui-spec';
import { materialAssemblyUiNodeSpec } from './material-assembly.ui-spec';
import { materialComparisonUiNodeSpec } from './material-comparison.ui-spec';
import { materialDisassemblyUiNodeSpec } from './material-disassembly.ui-spec';
import { booleanUiNodeSpec } from './boolean.ui-spec';
import { updateMaterialStatusUiNodeSpec } from './update-material-status.ui-spec';
import { notUiNodeSpec } from './not.ui-spec';
import { timestampUiNodeSpec } from './timestamp.ui-spec';

export * from './UiNodeSpec';

/**
 * a specification for how to initialize and work with a node, keyed by the node kind
 */
export const nodeKindMap: {
    [kind in NodeKind]: UiNodeSpec<kind>;
} = {
    appletEventEmit: appletEventEmitUiNodeSpec,
    appletEventListen: appletEventListenUiNodeSpec,
    attributeMaterialLink: attributeMaterialLinkUiNodeSpec,
    boolean: booleanUiNodeSpec,
    boolCast: boolCastUiNodeSpec,
    booleanComparison: booleanComparisonUiNodeSpec,
    comparisonNumber: comparisonNumberUiNodeSpec,
    comparisonString: comparisonStringUiNodeSpec,
    createMaterial: createMaterialUiNodeSpec,
    defaultVal: defaultValUiNodeSpec,
    eventDelay: eventDelayUiNodeSpec,
    eventOneShot: eventOneShotUiNodeSpec,
    eventSwitch: eventSwitchUiNodeSpec,
    eventThrottle: eventThrottleUiNodeSpec,
    fileRead: fileReadUiNodeSpec,
    fileWrite: fileWriteUiNodeSpec,
    materialAssembly: materialAssemblyUiNodeSpec,
    materialComparison: materialComparisonUiNodeSpec,
    materialDisassembly: materialDisassemblyUiNodeSpec,
    jsonAgg: jsonAggUiNodeSpec,
    jsonParse: jsonParseUiNodeSpec,
    mqttPublish: mqttPublishUiNodeSpec,
    mqttSubscribe: mqttSubscribeUiNodeSpec,
    numCast: numCastUiNodeSpec,
    numClamp: numClampUiNodeSpec,
    number: numberUiNodeSpec,
    performanceDataCollect: performanceDataCollectUiNodeSpec,
    processCollect: processCollectUiNodeSpec,
    script: scriptUiNodeSpec,
    slice: sliceUiNodeSpec,
    sparkplugCommander: sparkplugCommanderUiNodeSpec,
    sparkplugConsumer: sparkplugConsumerUiNodeSpec,
    start: startUiNodeSpec,
    strCast: strCastUiNodeSpec,
    text: textUiNodeSpec,
    timer: timerUiNodeSpec,
    txtFormat: txtFormatUiNodeSpec,
    udpRead: udpReadUiNodeSpec,
    udpSocket: udpSocketUiNodeSpec,
    udpWrite: udpWriteUiNodeSpec,
    varAssigned: varAssignedUiNodeSpec,
    varRead: varReadUiNodeSpec,
    varWrite: varWriteUiNodeSpec,
    varWriteMultiple: varWriteMultipleSpec,
    calendar: calendarUiNodeSpec,
    workInstruction: workInstructionUiNodeSpec,
    materialLookup: materialLookupUiNodeSpec,
    workOrder: workOrderUiNodeSpec,
    workOrderSetStatus: workOrderSetStatusUiNodeSpec,
    updateMaterialStatus: updateMaterialStatusUiNodeSpec,
    goToTab: goToTabUiNodeSpec,
    nextTab: nextTabUiNodeSpec,
    previousTab: previousTabUiNodeSpec,
    functionalInputs: functionalInputsUiNodeSpec,
    functionalSelect: functionalSelectUiNodeSpec,
    functionalOutputs: functionalOutputsUiNodeSpec,
    not: notUiNodeSpec,
    timestamp: timestampUiNodeSpec,
};
